// ============================================================
// Sidebar — labeled left navigation column with COLLAPSIBLE groups
// (v21 layout-density slice).
//
// Groups collapse/expand, remember their state in localStorage, and
// only Home / Command Center / Olivium start expanded — the long
// vertical scroll is gone. A group containing the active route
// auto-expands so navigation never hides your location. Group headers
// carry a powered-module count badge.
// ============================================================

import { COMMAND_CENTER_ROOT } from '../routes/CommandCenter/commandCenterRoutes'
import { useCallback, useEffect, useState, type CSSProperties } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useShell } from './ShellContext'
import { SHELL_TOKENS, text } from './styles'
import { COMMAND_MODEL } from './commandModel'

// The Square Code Command Center operating model. The sidebar is a pure
// projection of COMMAND_MODEL (shell/commandModel.ts) so a module is
// declared exactly once and stays in lockstep with the route table.
// This whole surface renders inside AccessGate — it is operator-gated.
const NAV = COMMAND_MODEL

const NAV_STATE_KEY = 'sc-nav-open-groups'
const DEFAULT_OPEN = ['home', 'command-center', 'olivium']

// Pinned quick actions — the operator's most-used surfaces, always at
// the very top of the rail regardless of group state (operator
// direction 2026-07-06: "the action items on the left are still too
// far down"). Every entry is a real, powered route.
const PINNED_ACTIONS: Array<{ key: string; label: string; glyph: string; path: string }> = [
  // PRIMARY entry (PHASE_2K deployment promotion): Command Center V2 is the
  // Square Code command center. Full-screen takeover mounted outside
  // MainframeShell (see router.tsx); the prefix match below covers every V2
  // child route (operations/projects/communications/portal-factory/calendar…).
  { key: 'pin-v2-command-center', label: 'Command Center', glyph: '◆', path: COMMAND_CENTER_ROOT },
  { key: 'pin-post-jobs', label: 'Post Job', glyph: '▣', path: '/square/command-center/operations' },
  { key: 'pin-meeting', label: 'Meeting', glyph: '◉', path: '/square/command-center/communications/conference-calls' },
  { key: 'pin-agents', label: 'Agents', glyph: '⬡', path: '/square/command-center/agents' },
  { key: 'pin-secure', label: 'Secure', glyph: '⬒', path: '/square/olivium/secure-access' },
  { key: 'pin-creative', label: 'Creative', glyph: '✦', path: '/square/command-center/workspaces/creative' },
  { key: 'pin-calendar', label: 'Calendar', glyph: '◈', path: '/square/command-center/calendar' },
  { key: 'pin-usage', label: 'Usage', glyph: '▤', path: '/square/command-center/operations' },
]

function readOpenGroups(): string[] {
  try {
    if (typeof window === 'undefined') return DEFAULT_OPEN
    const raw = window.localStorage.getItem(NAV_STATE_KEY)
    if (!raw) return DEFAULT_OPEN
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : DEFAULT_OPEN
  } catch {
    return DEFAULT_OPEN
  }
}

export default function Sidebar() {
  const navigate = useNavigate()
  const location = useLocation()
  const { clear } = useShell()
  const [openGroups, setOpenGroups] = useState<string[]>(DEFAULT_OPEN)

  useEffect(() => { setOpenGroups(readOpenGroups()) }, [])

  const toggleGroup = useCallback((key: string) => {
    setOpenGroups((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
      try { window.localStorage.setItem(NAV_STATE_KEY, JSON.stringify(next)) } catch { /* private mode */ }
      return next
    })
  }, [])

  return (
    <nav
      className="sc-shell-sidebar"
      aria-label="Primary"
      style={{
        borderRight: `1px solid ${SHELL_TOKENS.borderAlt}`,
        padding: '18px 12px 24px',
        background: 'rgba(8,8,12,0.62)',
        backdropFilter: 'blur(14px)',
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          ...text.kicker,
          fontSize: 9,
          letterSpacing: '0.22em',
          fontWeight: 800,
          color: SHELL_TOKENS.inkDim,
          padding: '0 8px 8px',
        }}
      >
        QUICK ACTIONS
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 6,
          marginBottom: 16,
          paddingBottom: 14,
          borderBottom: `1px solid ${SHELL_TOKENS.border}`,
        }}
      >
        {PINNED_ACTIONS.map((pin) => {
          const active = location.pathname === pin.path || location.pathname.startsWith(pin.path + '/')
          return (
            <button
              key={pin.key}
              type="button"
              onClick={() => { clear(); navigate(pin.path) }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                padding: '8px 9px',
                borderRadius: 8,
                border: `1px solid ${active ? SHELL_TOKENS.brandLine : SHELL_TOKENS.border}`,
                background: active ? `${SHELL_TOKENS.brand}1f` : SHELL_TOKENS.surfaceRaised,
                color: active ? SHELL_TOKENS.ink : SHELL_TOKENS.inkDim,
                cursor: 'pointer',
                fontSize: 11.5,
                fontWeight: 700,
                fontFamily: 'inherit',
                textAlign: 'left',
              }}
            >
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: active ? SHELL_TOKENS.brand : SHELL_TOKENS.inkFaint }}>
                {pin.glyph}
              </span>
              {pin.label}
            </button>
          )
        })}
      </div>

      {NAV.map((section, sIdx) => {
        const containsActive = section.items.some(
          (item) => location.pathname === item.path || location.pathname.startsWith(item.path + '/'),
        )
        const open = openGroups.includes(section.key) || containsActive
        const poweredCount = section.items.filter((i) => i.status === 'powered').length
        return (
          <div key={section.key} style={{ marginBottom: sIdx === NAV.length - 1 ? 0 : 10 }}>
            <button
              type="button"
              onClick={() => toggleGroup(section.key)}
              aria-expanded={open ? 'true' : 'false'}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                padding: '7px 8px',
                borderRadius: 7,
                fontFamily: 'inherit',
                textAlign: 'left',
              }}
            >
              <span
                aria-hidden
                style={{
                  fontSize: 8,
                  color: open ? SHELL_TOKENS.brand : SHELL_TOKENS.inkWhisper,
                  fontFamily: 'var(--font-mono)',
                  width: 10,
                  flexShrink: 0,
                }}
              >
                {open ? '▾' : '▸'}
              </span>
              <span
                style={{
                  ...text.kicker,
                  fontSize: 9.5,
                  letterSpacing: '0.2em',
                  fontWeight: 800,
                  color: open || containsActive ? SHELL_TOKENS.inkDim : SHELL_TOKENS.inkWhisper,
                }}
              >
                {section.label}
              </span>
              {poweredCount > 0 && (
                <span
                  style={{
                    marginLeft: 'auto',
                    fontSize: 8.5,
                    fontFamily: 'var(--font-mono)',
                    color: SHELL_TOKENS.inkWhisper,
                    border: `1px solid ${SHELL_TOKENS.border}`,
                    borderRadius: 999,
                    padding: '1px 6px',
                    flexShrink: 0,
                  }}
                >
                  {poweredCount}
                </span>
              )}
            </button>
            {open && section.items.map((item) => {
              const active =
                location.pathname === item.path ||
                location.pathname.startsWith(item.path + '/')
              return (
                <NavButton
                  key={item.key}
                  active={active}
                  glyph={item.glyph}
                  label={item.label}
                  onSelect={() => {
                    clear()
                    navigate(item.path)
                  }}
                />
              )
            })}
          </div>
        )
      })}
    </nav>
  )
}

function NavButton({
  active,
  glyph,
  label,
  onSelect,
}: {
  active: boolean
  glyph: string
  label: string
  onSelect: () => void
}) {
  // Brand Guide v2: nav item active = 3px accent bar on the left,
  // accent-lit icon square, label in primary ink weight 600,
  // subtle colored glow to mean "you are here".
  const style: CSSProperties = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    textAlign: 'left',
    marginBottom: 2,
    padding: '9px 12px 9px 14px',
    borderRadius: 8,
    border: '1px solid transparent',
    background: active
      ? SHELL_TOKENS.surfaceHigh
      : 'transparent',
    color: active ? SHELL_TOKENS.ink : SHELL_TOKENS.inkDim,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: active ? 600 : 400,
    fontFamily: 'inherit',
    transition: 'background 0.25s var(--sc-ease), color 0.25s',
    position: 'relative',
  }

  return (
    <button type="button" onClick={onSelect} style={style}>
      {active && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            left: 0,
            top: 8,
            bottom: 8,
            width: 3,
            borderRadius: 2,
            background: SHELL_TOKENS.brand,
            boxShadow: `0 0 8px ${SHELL_TOKENS.brandGlow}`,
          }}
        />
      )}
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 20,
          height: 20,
          borderRadius: 6,
          fontSize: 11,
          color: active ? SHELL_TOKENS.brand : SHELL_TOKENS.inkFaint,
          background: active ? `${SHELL_TOKENS.brand}1f` : SHELL_TOKENS.surfaceAlt,
          border: `1px solid ${active ? SHELL_TOKENS.brandLine : 'transparent'}`,
          flexShrink: 0,
          fontFamily: 'var(--sc-font-mono, var(--font-mono))',
        }}
      >
        {glyph}
      </span>
      {label}
    </button>
  )
}
